// src/components/workspace/IntelligenceCopilot.js
import React, { useState, useEffect, useMemo } from "react";
import { Lightbulb, Search, ClipboardList, CheckCircle2, AlertTriangle } from "lucide-react";

/**
 * Renders inline so it can be docked into the workspace communications rail.
 * It used to be a fixed-position floating sidebar with its own launcher button,
 * which overlapped the rest of the workspace whenever it was open.
 */
function IntelligenceCopilot({ 
  problem, 
  messages, 
  files, 
  updates, 
  notes, 
  currentUser,
  onTaskCloseCheck 
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [summary, setSummary] = useState("");
  const [errorSnippets, setErrorSnippets] = useState([]);
  const [lastSummaryUpdate, setLastSummaryUpdate] = useState(null);

  // Analyze messages for error codes and stack traces
  useEffect(() => {
    if (!messages || messages.length === 0) {
      setErrorSnippets([]);
      return;
    }

    const errorPatterns = [
      /error:\s*(\d+)/gi,
      /exception:\s*(.+)/gi,
      /stack trace:/gi,
      /at\s+.+\(.+:\d+:\d+\)/gi,
      /\[error\]/gi,
      /failed|failure|broken|crash/gi,
    ];

    const foundErrors = [];
    messages.forEach((msg) => {
      if (msg.type === "text" && msg.message) {
        errorPatterns.forEach((pattern) => {
          const matches = msg.message.match(pattern);
          if (matches) {
            foundErrors.push({
              message: msg.message.substring(0, 200),
              sender: msg.senderName,
              timestamp: msg.createdAt,
            });
          }
        });
      }
    });

    setErrorSnippets(foundErrors.slice(0, 3)); // Limit to 3 most recent
  }, [messages]);

  // Generate contextual suggestions based on tags
  useEffect(() => {
    if (!problem?.tags || problem.tags.length === 0) {
      setSuggestions([]);
      return;
    }

    const tagSuggestions = {
      firebase: "Verify Firebase credentials and check console for authentication errors.",
      python: "Review Python traceback and ensure all dependencies are installed.",
      javascript: "Check browser console for JavaScript errors and verify API endpoints.",
      react: "Inspect React DevTools and check for component lifecycle issues.",
      node: "Review server logs and verify environment variables are set correctly.",
      database: "Check database connection strings and query syntax.",
      api: "Verify API endpoints, authentication headers, and response formats.",
    };

    const newSuggestions = [];
    problem.tags.forEach((tag) => {
      const normalizedTag = tag.toLowerCase();
      if (tagSuggestions[normalizedTag]) {
        newSuggestions.push({
          tag: tag,
          tip: tagSuggestions[normalizedTag],
        });
      }
    });

    setSuggestions(newSuggestions);
  }, [problem?.tags]);

  // Generate conversation summary
  useEffect(() => {
    const generateSummary = () => {
      if (!messages || messages.length === 0) {
        setSummary("No conversation yet. Start chatting to generate a summary.");
        return;
      }

      const textMessages = messages.filter((m) => m.type === "text");
      if (textMessages.length === 0) {
        setSummary("No text messages to summarize.");
        return;
      }

      // Extract key information
      const clientMessages = textMessages.filter(
        (m) => m.senderId === problem.clientId
      );
      const helperMessages = textMessages.filter(
        (m) => m.senderId === problem.helperId
      );
      const hasFiles = files && files.length > 0;
      const hasUpdates = updates && updates.length > 0;

      // Build summary
      let summaryText = `Conversation Summary:\n\n`;
      summaryText += `• Total messages: ${textMessages.length} (${clientMessages.length} from client, ${helperMessages.length} from expert)\n`;
      if (hasFiles) {
        summaryText += `• Files shared: ${files.length}\n`;
      }
      if (hasUpdates) {
        summaryText += `• Progress updates: ${updates.length}\n`;
      }

      // Extract key topics from recent messages
      const recentMessages = textMessages.slice(-5);
      const topics = [];
      recentMessages.forEach((msg) => {
        const text = msg.message.toLowerCase();
        if (text.includes("error") || text.includes("issue") || text.includes("problem")) {
          topics.push("Issue identification");
        }
        if (text.includes("fix") || text.includes("solution") || text.includes("resolved")) {
          topics.push("Solution discussion");
        }
        if (text.includes("test") || text.includes("verify") || text.includes("check")) {
          topics.push("Testing/verification");
        }
      });

      if (topics.length > 0) {
        summaryText += `• Recent focus: ${[...new Set(topics)].join(", ")}\n`;
      }

      setSummary(summaryText);
      setLastSummaryUpdate(new Date());
    };

    generateSummary();

    // Auto-refresh every 10 minutes
    const interval = setInterval(generateSummary, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [messages, files, updates, problem]);

  // Task closer check - analyze if main problem terms were addressed
  const taskCloseCheck = useMemo(() => {
    if (!problem || !messages || messages.length === 0) {
      return null;
    }

    const problemKeywords = [
      ...(problem.title?.toLowerCase().split(/\s+/) || []),
      ...(problem.description?.toLowerCase().split(/\s+/) || []),
      ...(problem.tags || []).map((t) => t.toLowerCase()),
    ].filter((k) => k.length > 3); // Filter out short words

    const allMessages = messages
      .filter((m) => m.type === "text" && m.message)
      .map((m) => m.message.toLowerCase())
      .join(" ");

    const addressedKeywords = problemKeywords.filter((keyword) =>
      allMessages.includes(keyword)
    );

    const coverage = problemKeywords.length > 0
      ? (addressedKeywords.length / problemKeywords.length) * 100
      : 0;

    if (coverage < 50 && messages.length > 3) {
      return {
        warning: true,
        message: "Possible issues still unresolved. Key problem terms may not have been fully addressed in the conversation.",
        coverage: Math.round(coverage),
      };
    }

    return {
      warning: false,
      message: "Main problem terms appear to be addressed in the conversation.",
      coverage: Math.round(coverage),
    };
  }, [problem, messages]);

  // Expose task close check to parent
  useEffect(() => {
    if (onTaskCloseCheck && taskCloseCheck) {
      onTaskCloseCheck(taskCloseCheck);
    }
  }, [taskCloseCheck, onTaskCloseCheck]);

  return (
    <div className="h-full min-h-0 overflow-y-auto bg-white">
      <div className="px-4 py-4">

              {/* Suggestions Section */}
              {suggestions.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
                    <Lightbulb className="w-4 h-4 text-slate-400" />
                    Suggested Next Steps
                  </h3>
                  <div className="space-y-2">
                    {suggestions.map((suggestion, idx) => (
                      <div
                        key={idx}
                        className="bg-blue-50 border border-blue-200 rounded-lg p-3"
                      >
                        <p className="text-xs font-semibold text-blue-900 mb-1">
                          {suggestion.tag}
                        </p>
                        <p className="text-xs text-blue-800">
                          {suggestion.tip}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Error Detection Section */}
              {errorSnippets.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
                    <Search className="w-4 h-4 text-slate-400" />
                    Detected Issues
                  </h3>
                  <div className="space-y-2">
                    {errorSnippets.map((error, idx) => (
                      <div
                        key={idx}
                        className="bg-rose-50 border border-rose-200 rounded-lg p-3"
                      >
                        <p className="text-xs font-semibold text-rose-900 mb-1">
                          From {error.sender}
                        </p>
                        <p className="text-xs text-rose-800 font-mono">
                          {error.message}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Summary Section */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                    <ClipboardList className="w-4 h-4 text-slate-400" />
                    Intelligence Summary
                  </h3>
                  {lastSummaryUpdate && (
                    <button
                      onClick={() => {
                        // Force refresh summary
                        setLastSummaryUpdate(new Date());
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Refresh
                    </button>
                  )}
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <pre className="text-xs text-slate-700 whitespace-pre-wrap font-sans">
                    {summary}
                  </pre>
                  {lastSummaryUpdate && (
                    <p className="text-xs text-slate-500 mt-2">
                      Updated: {lastSummaryUpdate.toLocaleTimeString()}
                    </p>
                  )}
                </div>
              </div>

              {/* Task Closer Check */}
              {taskCloseCheck && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4 text-slate-400" />
                    Task Completion Check
                  </h3>
                  <div
                    className={`border rounded-lg p-4 ${
                      taskCloseCheck.warning
                        ? "bg-amber-50 border-amber-200"
                        : "bg-emerald-50 border-emerald-200"
                    }`}
                  >
                    <p
                      className={`text-xs font-semibold mb-1 flex items-center gap-1.5 ${
                        taskCloseCheck.warning
                          ? "text-amber-900"
                          : "text-emerald-900"
                      }`}
                    >
                      {taskCloseCheck.warning ? (
                        <>
                          <AlertTriangle className="w-3.5 h-3.5" />
                          Warning
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          Ready
                        </>
                      )}
                    </p>
                    <p
                      className={`text-xs ${
                        taskCloseCheck.warning
                          ? "text-amber-800"
                          : "text-emerald-800"
                      }`}
                    >
                      {taskCloseCheck.message}
                    </p>
                    <p className="text-xs text-slate-600 mt-2">
                      Coverage: {taskCloseCheck.coverage}%
                    </p>
                  </div>
                </div>
              )}
      </div>
    </div>
  );
}

export default IntelligenceCopilot;

